import '../styles.scss';
import React from 'react';
import PropTypes from 'prop-types';
import { Container } from '@edx/paragon';
import { injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import { getBusinessSeatUrl, isArchived } from '@utils/course';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import { useIsShowing } from '@utils/cohesion/hooks/useIsShown';
import tagularEvent from '@utils/cohesion/helpers/TagularEvent';
import { hyphenateForTagular } from '@utils/cohesion/helpers';
import messages from '../messages';

import PurchaseBtn from '../components/PurchaseBtn';
import RequestBtn from '../components/RequestBtn';

function BusinessBlockDesktop({
  intl, activeCourseRun, title, subjects,
}) {
  const isBrowser = typeof window !== 'undefined';

  const generatePurchasePayload = () => {
    const webElement = {
      elementType: 'BUTTON',
      location: 'enterprise-solutions-asset',
      htmlId: 'purchase-now-CTA',
      position: '1',
      text: 'Purchase Now',
      name: subjects.length > 0 ? hyphenateForTagular(subjects[0].name) : 'not-available',
      variant: 'courses',
    };

    return {
      webElement,
      webContext: {
        page: {
          title,
          url: isBrowser ? window.location.href : '',
          pageType: 'product-page',
        },
      },
    };
  };

  const purchaseRef = useIsShowing(() => {
    tagularEvent('ElementViewed', generatePurchasePayload());
  });

  const generateRequestPayload = () => {
    const webElement = {
      elementType: 'BUTTON',
      location: 'enterprise-solutions-asset',
      htmlId: 'request-info-CTA',
      position: '2',
      text: 'Request Information',
      name: subjects.length > 0 ? hyphenateForTagular(subjects[0].name) : 'not-available',
      variant: 'courses',
    };

    return {
      webElement,
      webContext: {
        page: {
          title,
          url: isBrowser ? window.location.href : '',
          pageType: 'product-page',
        },
      },
    };
  };

  const requestRef = useIsShowing(() => {
    tagularEvent('ElementViewed', generateRequestPayload());
  });

  const purchaseClick = () => {
    tagularEvent('ElementClicked', generatePurchasePayload());
  };

  const requestClick = () => {
    tagularEvent('ElementClicked', generateRequestPayload());
  };

  return (
    <Container size="sm" className="business-block">
      <div className="row">
        <div className="col-12 col-lg-3">
          <div className="title">
            <h2 className="title-content text-primary-500 mr-lg-4">
              {intl.formatMessage(messages['prospectus.course.business.title'])}
            </h2>
          </div>
        </div>
        <div className="col-12 col-lg-9 d-flex justify-content-center flex-column text-primary-500">
          <div className="cta-block">
            {intl.formatMessage(messages['prospectus.course.business_message'])}
            <div className="d-flex flex-column flex-sm-row mt-3">
              <div ref={purchaseRef}>
                <PurchaseBtn
                  businessSeatUrl={getBusinessSeatUrl(activeCourseRun)}
                  isArchived={isArchived(activeCourseRun)}
                  className="btn btn-primary mt-3 mt-sm-auto mr-3"
                  onClick={purchaseClick}
                />
              </div>
              <div ref={requestRef}>
                <RequestBtn
                  className="btn btn-outline-primary"
                  onClick={requestClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}

BusinessBlockDesktop.propTypes = {
  intl: intlShape.isRequired,
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  title: PropTypes.string.isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
};

BusinessBlockDesktop.defaultProps = {
  subjects: [],
};

export default injectIntl(BusinessBlockDesktop);
